/**
 * Custom hook for User Manual
 * Handles state and API calls for manual data
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { rbacService } from '@/services/rbac/rbacService';
import type { Feature, Action } from '@/types/rbac';
import type { MenuItem, ManualSection, IconInfo } from '@/types/userManual';
import { ROUTES } from '@/constants/routes';
import * as HeroIcons from '@heroicons/react/24/outline';
import {
  Cog6ToothIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import {
  Cog6ToothIcon as Cog6ToothIconSolid,
  BriefcaseIcon as BriefcaseIconSolid,
} from '@heroicons/react/24/solid';
import {
  fleetIcon,
  fleetIconSolid,
  statisticsIcon,
  statisticsIconSolid,
  tripsIcon,
  tripsIconSolid,
  userIcon,
  userIconSolid,
  searchIcon,
  notificationIcon,
  moonIcon,
  sunIcon,
  manualIcon,
} from '@/assets/icons/icons';
import {
  buildManualPages,
  iconCategories,
} from '@/constants/manualPages';

// Map feature to menu item
const featureToMenuItem = (feature: Feature, actions: Action[] = []): MenuItem | null => {
  const iconMap: Record<string, { icon: JSX.Element; iconSolid: JSX.Element }> = {
    DASHBOARD: { icon: tripsIcon, iconSolid: tripsIconSolid },
    FLEET: { icon: fleetIcon, iconSolid: fleetIconSolid },
    STATISTICS: { icon: statisticsIcon, iconSolid: statisticsIconSolid },
    REPORTS: { icon: statisticsIcon, iconSolid: statisticsIconSolid },
    ORGANISATION: { icon: userIcon, iconSolid: userIconSolid },
    PROFILE: { icon: userIcon, iconSolid: userIconSolid },
    CRM: { icon: <BriefcaseIcon className="w-5 h-5" />, iconSolid: <BriefcaseIconSolid className="w-5 h-5" /> },
    CRM_ECONOMY: { icon: <BriefcaseIcon className="w-5 h-5" />, iconSolid: <BriefcaseIconSolid className="w-5 h-5" /> },
    ADMIN: { icon: <Cog6ToothIcon className="w-5 h-5" />, iconSolid: <Cog6ToothIconSolid className="w-5 h-5" /> },
    SYSADMIN: { icon: <Cog6ToothIcon className="w-5 h-5" />, iconSolid: <Cog6ToothIconSolid className="w-5 h-5" /> },
  };

  const icons = iconMap[feature.code];
  if (!icons) return null;

  // Map route
  let route = feature.route;
  if (feature.code === 'DASHBOARD') route = ROUTES.DASHBOARD;
  if (feature.code === 'FLEET') route = ROUTES.FLEET;
  if (feature.code === 'STATISTICS') route = ROUTES.STATISTICS;
  if (feature.code === 'REPORTS') route = ROUTES.REPORTS;
  if (feature.code === 'PROFILE') route = ROUTES.PROFILE;
  if (feature.code === 'CRM') route = ROUTES.CRM;
  if (feature.code === 'CRM_ECONOMY') route = ROUTES.CRM_ECONOMY_OVERVIEW;
  if (feature.code === 'ADMIN') route = ROUTES.ADMIN;
  if (feature.code === 'SYSADMIN') route = ROUTES.SYSADMIN;

  return {
    ...icons,
    label: feature.name,
    path: route,
    featureCode: feature.code,
    description: feature.description,
    actions: actions.filter(a => a.feature_id === feature.id),
  };
};

export const useUserManual = (t: (key: string) => string) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [allActions, setAllActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Load manual data
  useEffect(() => {
    const loadManualData = async () => {
      try {
        setLoading(true);
        const featuresData = await rbacService.getFeatures();
        const activeFeatures = featuresData.filter(f => f.is_active);
        setFeatures(activeFeatures);

        const actions: Action[] = [];
        for (const feature of activeFeatures) {
          try {
            const featureActions = await rbacService.getActionsByFeature(feature.id);
            actions.push(...featureActions);
          } catch {
            // Ignore errors
          }
        }
        setAllActions(actions);
      } catch {
        // Error loading manual data
      } finally {
        setLoading(false);
      }
    };

    loadManualData();
  }, []);

  // Create menu items from features
  const menuItems: MenuItem[] = useMemo(() => {
    return features
      .map(feature => {
        const actions = allActions.filter(a => a.feature_id === feature.id);
        return featureToMenuItem(feature, actions);
      })
      .filter((item): item is MenuItem => item !== null)
      .sort((a, b) => {
        const orderA = features.find(f => f.code === a.featureCode)?.display_order || 0;
        const orderB = features.find(f => f.code === b.featureCode)?.display_order || 0;
        return orderA - orderB;
      });
  }, [features, allActions]);

  // Organize sections
  const manualSections: ManualSection[] = useMemo(() => {
    const pages = buildManualPages(t);
    const sections: ManualSection[] = [
      {
        title: t('manual.mainMenu'),
        items: menuItems.filter(item => 
          !['ADMIN', 'SYSADMIN', 'CRM', 'CRM_ECONOMY'].includes(item.featureCode)
        ),
      },
      {
        title: t('copy.ui.manualSectionTools'),
        items: pages.other,
      },
      {
        title: t('reports.title'),
        items: pages.reports,
      },
      {
        title: t('copy.ui.manualSectionCrm'),
        items: pages.crm,
      },
      {
        title: t('modules.booking.title'),
        items: pages.booking,
      },
      {
        title: t('manual.administration'),
        items: pages.admin,
      },
      {
        title: t('modules.sysadmin.title'),
        items: pages.sysadmin,
      },
    ];

    return sections.filter(section => section.items.length > 0);
  }, [menuItems, t]);

  // Filter based on search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) {
      return manualSections;
    }

    const query = searchQuery.toLowerCase();
    return manualSections.map(section => ({
      ...section,
      items: section.items.filter(item => {
        const matchesLabel = item.label.toLowerCase().includes(query);
        const matchesDescription = item.description?.toLowerCase().includes(query);
        const matchesActions = item.actions?.some(action => 
          action.name.toLowerCase().includes(query) || 
          action.description?.toLowerCase().includes(query)
        );
        return matchesLabel || matchesDescription || matchesActions;
      }),
    })).filter(section => section.items.length > 0);
  }, [manualSections, searchQuery]);

  // Available icons
  const availableIcons: IconInfo[] = useMemo(() => {
    const iconList: IconInfo[] = [];

    // Add icons from Heroicons
    Object.entries(iconCategories).forEach(([category, iconNames]) => {
      iconNames.forEach(iconName => {
        const IconComponent = (HeroIcons as Record<string, React.ComponentType<{ className?: string }>>)[iconName];
        if (IconComponent) {
          iconList.push({
            name: iconName,
            icon: <IconComponent className="w-5 h-5" />,
            description: t(`icons.${iconName.toLowerCase().replace('icon', '')}`) || iconName,
            category: category,
          });
        }
      });
    });

    // Add system icons
    iconList.push(
      { name: 'FleetIcon (used)', icon: fleetIcon, description: t('icons.fleet'), category: 'navigation' },
      { name: 'StatisticsIcon (used)', icon: statisticsIcon, description: t('icons.statistics'), category: 'navigation' },
      { name: 'TripsIcon (used)', icon: tripsIcon, description: t('icons.trips'), category: 'navigation' },
      { name: 'UserIcon (used)', icon: userIcon, description: t('icons.user'), category: 'navigation' },
      { name: 'ManualIcon (used)', icon: manualIcon, description: t('icons.manual'), category: 'navigation' },
      { name: 'SearchIcon (used)', icon: searchIcon, description: t('icons.search'), category: 'actions' },
      { name: 'NotificationIcon (used)', icon: notificationIcon, description: t('icons.notification'), category: 'actions' },
      { name: 'MoonIcon (used)', icon: moonIcon, description: t('icons.moon'), category: 'actions' },
      { name: 'SunIcon (used)', icon: sunIcon, description: t('icons.sun'), category: 'actions' },
    );

    return iconList.sort((a, b) => a.name.localeCompare(b.name));
  }, [t]);

  // Toggle section
  const toggleSection = useCallback((title: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(title)) {
        newSet.delete(title);
      } else {
        newSet.add(title);
      }
      return newSet;
    });
  }, []);

  return {
    loading,
    searchQuery,
    setSearchQuery,
    expandedSections,
    filteredSections,
    availableIcons,
    toggleSection,
  };
};
